// API Base URL - Configure this in your .env file
const API_URL = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) 
  ? (import.meta as any).env.VITE_API_URL 
  : 'http://localhost:5000/api';

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// API call helper with error handling
export const apiCall = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = getAuthToken();
  
  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 401) {
        // Don't redirect for login/auth endpoints - let them handle their own errors
        const isAuthEndpoint = endpoint.includes('/auth/login') || 
                               endpoint.includes('/auth/register') ||
                               endpoint.includes('/auth/forgot-password') ||
                               endpoint.includes('/auth/verify') ||
                               endpoint.includes('/auth/reset-password');
        
        if (!isAuthEndpoint) {
          // Token expired or invalid - redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminAuth');
          window.location.href = '/login';
        }
      }
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// ============================================
// AUTH API
// ============================================
export const authAPI = {
  login: (email: string, password: string) =>
    apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  
  register: (data: { name: string; email: string; password: string; confirmPassword: string }) =>
    apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  verifyRegistrationOTP: (email: string, otp: string) =>
    apiCall('/auth/verify-registration-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    }),
  
  forgotPassword: (email: string) =>
    apiCall('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  
  verifyForgotPasswordOTP: (email: string, otp: string) =>
    apiCall('/auth/verify-forgot-password-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    }),
  
  resetPassword: (resetToken: string, password: string, confirmPassword: string) =>
    apiCall(`/auth/reset-password/${resetToken}`, {
      method: 'PUT',
      body: JSON.stringify({ password, confirmPassword }),
    }),
  
  logout: () => apiCall('/auth/logout', { method: 'POST' }),
  
  getMe: () => apiCall('/auth/me'),
  
  updateDetails: (data: { name?: string; email?: string; avatar?: string }) =>
    apiCall('/auth/updatedetails', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  updatePassword: (currentPassword: string, newPassword: string) =>
    apiCall('/auth/updatepassword', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  
  getAdmins: () => apiCall('/auth/admins'),
  
  getPendingRegistrations: () => apiCall('/auth/pending-registrations'),
  
  deleteAdmin: (id: string) =>
    apiCall(`/auth/admin/${id}`, { method: 'DELETE' }),
  
  toggleAdminStatus: (id: string) =>
    apiCall(`/auth/admin/${id}/toggle-status`, { method: 'PATCH' }),
};

// ============================================
// ENQUIRIES API
// ============================================
export interface EnquiryParams {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  search?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
  archived?: boolean;
}

export interface EnquiryData {
  name: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  subject: string;
  message: string;
  status?: string;
  priority?: string;
  notes?: string;
  tags?: string[];
  visibility?: 'visible' | 'hidden';
}

export const enquiriesAPI = {
  getAll: (params?: EnquiryParams) => {
    const queryString = params ? `?${new URLSearchParams(params as any)}` : '';
    return apiCall(`/enquiries${queryString}`);
  },
  
  getOne: (id: string) => apiCall(`/enquiries/${id}`),
  
  create: (data: EnquiryData) =>
    apiCall('/enquiries', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<EnquiryData>) =>
    apiCall(`/enquiries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    apiCall(`/enquiries/${id}`, { method: 'DELETE' }),
  
  getStats: () => apiCall('/enquiries/stats'),
  
  archive: (id: string) =>
    apiCall(`/enquiries/${id}/archive`, { method: 'PATCH' }),
};

// ============================================
// APPLICATIONS API
// ============================================
export interface ApplicationParams {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  search?: string;
  position?: string;
}

export interface ApplicationData {
  name: string;
  email: string;
  phone: string;
  position: string;
  careerId?: string;
  experience: string;
  coverLetter?: string;
  resumeUrl?: string;
  resumeFileName?: string;
  skills?: string[];
  linkedinUrl?: string;
  portfolioUrl?: string;
  expectedSalary?: string;
  availableFrom?: string;
  status?: string;
  priority?: string;
  notes?: string;
  // Personal Information
  dob?: string;
  gender?: string;
  currentAddress?: string;
  location?: string;
  // Experience Details
  experienceLevel?: string;
  totalExperience?: string;
  previousJobTitle?: string;
  currentCtc?: string;
  noticePeriod?: string;
  preferredLocation?: string;
  willingToRelocate?: string;
  employmentType?: string;
  // Education Details
  education?: string;
  qualification?: string;
  specialization?: string;
  university?: string;
  yearOfPassing?: string;
  percentageCgpa?: string;
  // Rating
  rating?: number;
  visibility?: 'visible' | 'hidden';
}

export const applicationsAPI = {
  getAll: (params?: ApplicationParams) => {
    const queryString = params ? `?${new URLSearchParams(params as any)}` : '';
    return apiCall(`/applications${queryString}`);
  },
  
  getOne: (id: string) => apiCall(`/applications/${id}`),
  
  create: (data: ApplicationData) =>
    apiCall('/applications', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<ApplicationData>) =>
    apiCall(`/applications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    apiCall(`/applications/${id}`, { method: 'DELETE' }),
  
  getStats: () => apiCall('/applications/stats'),
};

// ============================================
// RESUMES API
// ============================================
export interface ResumeParams {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  search?: string;
  position?: string;
}

export interface ResumeData {
  name: string;
  email: string;
  phone: string;
  position: string;
  experience: string;
  resumeUrl: string;
  resumeFileName?: string;
  resumeFileSize?: number;
  skills?: string[];
  linkedinUrl?: string;
  portfolioUrl?: string;
  location?: string;
  expectedSalary?: string;
  status?: string;
  priority?: string;
  notes?: string;
  rating?: number;
  visibility?: 'visible' | 'hidden';
}

export const resumesAPI = {
  getAll: (params?: ResumeParams) => {
    const queryString = params ? `?${new URLSearchParams(params as any)}` : '';
    return apiCall(`/resumes${queryString}`);
  },
  
  getOne: (id: string) => apiCall(`/resumes/${id}`),
  
  create: (data: ResumeData) =>
    apiCall('/resumes', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<ResumeData>) =>
    apiCall(`/resumes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    apiCall(`/resumes/${id}`, { method: 'DELETE' }),
  
  getStats: () => apiCall('/resumes/stats'),
};

// ============================================
// CAREERS API
// ============================================
export interface CareerParams {
  page?: number;
  limit?: number;
  status?: string;
  department?: string;
  type?: string;
  search?: string;
}

export interface CareerData {
  title: string;
  department: string;
  location: string;
  type?: string;
  experience?: string;
  description: string;
  requirements?: string[];
  responsibilities?: string[];
  skills?: string[];
  salaryRange?: {
    min: number;
    max: number;
    currency: string;
  };
  benefits?: string[];
  status?: string;
  featured?: boolean;
  applicationDeadline?: string;
  expirationDate?: string;
  postedDate?: string;
  workMode?: string;
  salary?: string;
  jobSummary?: string;
  preferredQualifications?: string[];
  teamInfo?: string;
  relocationSupport?: boolean;
  numberOfOpenings?: number;
  tags?: string[];
}

export const careersAPI = {
  // Public endpoints (no auth required) - for website/public pages
  getAll: (params?: CareerParams) => {
    const queryString = params ? `?${new URLSearchParams(params as any)}` : '';
    return apiCall(`/careers/public${queryString}`);
  },
  
  getOne: (id: string) => apiCall(`/careers/public/${id}`),
  
  // Admin endpoints (auth required) - for admin dashboard
  // Returns all items for super_admin (including hidden), only visible for admin
  getAllAdmin: (params?: CareerParams) => {
    const queryString = params ? `?${new URLSearchParams(params as any)}` : '';
    return apiCall(`/careers${queryString}`);
  },
  
  getOneAdmin: (id: string) => apiCall(`/careers/${id}`),
  
  create: (data: CareerData) =>
    apiCall('/careers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<CareerData>) =>
    apiCall(`/careers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    apiCall(`/careers/${id}`, { method: 'DELETE' }),
  
  getStats: () => apiCall('/careers/admin/stats'),
};

// ============================================
// SETTINGS API
// ============================================
export interface SettingData {
  value: any;
  category?: string;
  description?: string;
}

export const settingsAPI = {
  getAll: (category?: string) => {
    const queryString = category ? `?category=${category}` : '';
    return apiCall(`/settings${queryString}`);
  },
  
  getOne: (key: string) => apiCall(`/settings/${key}`),
  
  update: (key: string, data: SettingData) =>
    apiCall(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (key: string) =>
    apiCall(`/settings/${key}`, { method: 'DELETE' }),
};

// ============================================
// AI API
// ============================================
export interface AIJobGenerationRequest {
  prompt: string;
}

export interface AIJobGenerationResponse {
  success: boolean;
  message: string;
  data: {
    title: string;
    department: string;
    location: string;
    workMode: string;
    type: string;
    experience: string;
    salary: string;
    description: string;
    jobSummary: string;
    responsibilities: string[];
    requirements: string[];
    qualifications: string[];
    benefits: string[];
    skills: string[];
  };
}

export const aiAPI = {
  generateJobDescription: (prompt: string): Promise<AIJobGenerationResponse> =>
    apiCall('/ai/generate-job', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    }),
};

// ============================================
// UTILITY FUNCTIONS
// ============================================
export const setAuthToken = (token: string) => {
  localStorage.setItem('authToken', token);
};

export const removeAuthToken = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('adminUser');
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

// Export API URL for direct use if needed
export { API_URL };